import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  clearSelectedBpRoutine,
  fetchFavouritesRoutine,
  toggleFavouriteRoutine
} from 'features/bpList/ducks/actions'
import {
  getFavourites,
  getFavouritesCount,
  getFavouritesTotalPages
} from 'features/bpList/ducks/selectors'
import styled, { keyframes } from 'styled-components'
import PageHeader from 'components/atoms/PageHeader'
import Table from 'features/bpList/Components/Table'
import { useTranslation } from 'react-i18next'
import Pagination from 'components/atoms/Pagination'
import Filters from 'features/filters/Filters'
import { getFiltersQuery } from 'utils/filters'
import { isNotNilOrEmpty } from 'utils/ramda'
import { not, values, equals, isEmpty } from 'ramda'
import Drawer from '@mui/material/Drawer'
import FilterListIcon from '@mui/icons-material/FilterList'
import EmptyBpList from 'features/bpList/Components/EmptyBpsListState'
import CircularProgress from '@mui/material/CircularProgress'
import { selectIsFetchingFavourites } from 'ducks/loaderSelectors'
import { clearBpFilesRoutine } from 'ducks/files/actions'
import { useNavigate } from 'react-router-dom'
import useMediaQuery from '@mui/material/useMediaQuery'
import { formatDate } from 'utils/date'
import useBodyScrollLock from 'hooks/useBodyScrollLock'

import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'

const defaultFilters = {
  status: '',
  owner: [],
  responsible_person: [],
  created_at: null,
  updated_at: null,
  building_types: []
}

const getUserCompany = (u) => {
  if (!u || typeof u === 'string') return null
  const c = (u.company || u.company_name || '').trim()
  return c || null
}

const getBpCompanies = (bp) => {
  const users = Array.isArray(bp?.owners) ? bp.owners : []
  const companies = users.map(getUserCompany).filter(Boolean)
  return [...new Set(companies)]
}

const ShortlistedBps = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const bps = useSelector(getFavourites)
  const pagesCount = useSelector(getFavouritesTotalPages)
  const totalFound = useSelector(getFavouritesCount)
  const isLoading = useSelector(selectIsFetchingFavourites)

  const [isDrawerOpen, setDrawerOpen] = useState(false)
  const [filters, setFilters] = useState(defaultFilters)
  const [sortingOptions, setSortingOptions] = useState({
    column: 'name',
    desc: false,
    page: 1
  })

  const isMobile = useMediaQuery('(max-width: 1024px)')

  useBodyScrollLock(isDrawerOpen && isMobile)

  useEffect(() => {
    const { page, desc, column } = sortingOptions
    dispatch(fetchFavouritesRoutine({
      sort: desc ? `-${column}` : column,
      filters: getFiltersQuery(filters),
      page
    }))
  }, [sortingOptions, filters, dispatch])

  const handleChangeSorting = column => () => {
    setSortingOptions(prev => ({
      ...prev,
      column,
      desc: column === prev.column ? !prev.desc : false
    }))
  }

  const handlePageChange = page => {
    setSortingOptions(prev => ({ ...prev, page }))
  }

  const openDrawer = () => setDrawerOpen(true)
  const closeDrawer = () => setDrawerOpen(false)
  const handleClearFilters = () => setFilters(defaultFilters)

  const shouldRenderClearButton = useMemo(() => {
    return values(filters).some(e => isNotNilOrEmpty(e)) && not(equals(filters, defaultFilters))
  }, [filters])

  useEffect(() => {
    dispatch(clearSelectedBpRoutine())
    dispatch(clearBpFilesRoutine())
  }, [dispatch])

  const handleCardClick = (id) => {
    navigate(`/bp-list/${id}`)
  }

  const handleToggleFavourite = (e, bp) => {
    e.stopPropagation()
    const isFav = bp.is_favourite || bp.isFavourite || true
    dispatch(toggleFavouriteRoutine({
      id: bp.id,
      isFavourite: isFav
    }))
  }

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return { bg: '#e6f7ff', text: '#1890ff' }
      case 'inactive': return { bg: '#fff1f0', text: '#f5222d' }
      case 'dormant': return { bg: '#fffbe6', text: '#faad14' }
      default: return { bg: '#f5f5f5', text: '#8c8c8c' }
    }
  }

  const BpCard = ({ bp }) => {
    const statusStyle = getStatusColor(bp.status)
    const uprn = bp.uprn || 'Not yet allocated'
    const address = [bp.street, bp.city].filter(Boolean).join(', ') || '-'
    const companies = getBpCompanies(bp)
    const companyLabel = companies.length ? companies.join(', ') : '-'
    const isFav = bp.is_favourite || bp.isFavourite || true

    return (
      <UnifiedCard onClick={() => handleCardClick(bp.id)}>
        <CardHeader>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', flex: 1 }}>
            <FavIconWrapper onClick={(e) => handleToggleFavourite(e, bp)} $isActive={isFav}>
              {isFav ? <StarIcon /> : <StarBorderIcon />}
            </FavIconWrapper>
            <CardTitle>{bp.name}</CardTitle>
          </div>
          {bp.status && (
            <StatusBadgeMobile bg={statusStyle.bg} text={statusStyle.text}>
              {bp.status}
            </StatusBadgeMobile>
          )}
        </CardHeader>
        <CardBody>
          <InfoGrid>
            <InfoItem>
              <Label>UPRN</Label>
              <Value>{uprn}</Value>
            </InfoItem>
            <InfoItem>
              <Label>Address</Label>
              <Value>{address}</Value>
            </InfoItem>
            <InfoItem>
              <Label>Postcode</Label>
              <Value>{bp.postalCode || '-'}</Value>
            </InfoItem>
            <InfoItem>
              <Label>Company</Label>
              <Value title={companyLabel}>{companyLabel}</Value>
            </InfoItem>
            <InfoItem>
              <Label>Created</Label>
              <Value>{formatDate(bp.createdAt)}</Value>
            </InfoItem>
          </InfoGrid>
        </CardBody>
      </UnifiedCard>
    )
  }

  return (
    <PageWrapper>
      <ContentWrapper>
        <PageHeader>My Favourites</PageHeader>

        <ControlsRow>
          <ResultInfo>
            <BpsCount>
              {totalFound > 0 && !isLoading && (
                <>
                  <span>{totalFound}&nbsp;</span>
                  {t('bpList.countResults')}
                </>
              )}
            </BpsCount>
          </ResultInfo>

          <ActionsGroup>
            {shouldRenderClearButton && (
              <ClearFiltersButton onClick={handleClearFilters}>
                {t('bpList.clearFilters')}
              </ClearFiltersButton>
            )}

            <UnifiedFilterButton onClick={openDrawer}>
              <FiltersIcon />
              <FilterText>Filters</FilterText>
            </UnifiedFilterButton>
          </ActionsGroup>
        </ControlsRow>

        {isLoading ? (
          <Loading><CircularProgress /></Loading>
        ) : isEmpty(bps) && equals(defaultFilters, filters) ? (
          <EmptyBpList favourites />
        ) : (
          <>
            <MobileView>
              <CardsGrid>
                {bps.map((bp) => (
                  <BpCard key={bp.id} bp={bp} />
                ))}
              </CardsGrid>
            </MobileView>

            <DesktopView>
              <Table
                sortingOptions={sortingOptions}
                sortingChange={handleChangeSorting}
                bps={bps}
                isFavouritesTable
              />
            </DesktopView>
          </>
        )}

        <Pagination onChange={handlePageChange} totalPages={pagesCount} />
      </ContentWrapper>

      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        variant={isMobile ? 'temporary' : 'persistent'}
        open={isDrawerOpen}
        onClose={closeDrawer}
        disableScrollLock={isMobile}
        style={{ zIndex: 1200 }}
        PaperProps={{
          sx: {
            boxSizing: 'border-box',
            ...(isMobile ? {
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
              maxHeight: '85vh',
              width: '100%',
              maxWidth: '100vw',
              margin: 0
            } : {
              width: '300px',
              border: 'none',
              top: '88px',
              height: 'calc(100% - 88px)',
              boxShadow: '-5px 0 15px rgba(0,0,0,0.05)'
            })
          }
        }}
      >
        <FiltersWrapper>
          <MobileDrawerHandle />
          <Filters
            closeDrawer={closeDrawer}
            parentFilters={filters}
            getFilters={setFilters}
            defaultFilters={defaultFilters}
          />
        </FiltersWrapper>
      </Drawer>
    </PageWrapper>
  )
}

export default ShortlistedBps

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`

const PageWrapper = styled.div`
  display: flex;
  padding-bottom: 50px;
  width: 100%;
  animation: ${fadeIn} 0.3s ease-out;
  box-sizing: border-box;
  overflow-x: hidden;
`

const ContentWrapper = styled.div`
  width: 100%;
  padding-right: 0;
  box-sizing: border-box;
  min-width: 0;
`

const ControlsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`

const ResultInfo = styled.div`
  display: flex;
  align-items: center;
`

const ActionsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
`

const FiltersWrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.lighterGrey};
  padding: 25px 20px;
  overflow-y: auto;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    background-color: #fff;
    padding: 10px 20px 30px;
  }
`

const MobileDrawerHandle = styled.div`
  display: none;
  @media (max-width: 1024px) {
    display: block;
    width: 40px;
    height: 4px;
    background-color: #e0e0e0;
    border-radius: 2px;
    margin: 0 auto 20px auto;
  }
`

const BpsCount = styled.div`
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 13px;
  span {
    font-weight: bold;
  }
`

const ClearFiltersButton = styled.button`
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 13px;
  font-weight: 500;
  text-transform: uppercase;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 8px;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.primary}10;
  }
`

const UnifiedFilterButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px ${({ theme }) => theme.colors.primary}30;

  &:hover {
    background: ${({ theme }) => theme.colors.secondary};
    transform: translateY(-2px);
  }

  @media (max-width: 1024px) {
    width: 36px;
    height: 36px;
    padding: 0;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  }
`

const FilterText = styled.span`
  margin-left: 8px;
  font-weight: 500;

  @media (max-width: 1024px) {
    display: none;
  }
`

const FiltersIcon = styled(FilterListIcon)`
  font-size: 20px !important;
  color: inherit;
`

const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 200px;
`

const MobileView = styled.div`
  display: none;
  width: 100%;
  @media (max-width: 1024px) {
    display: block;
  }
`

const DesktopView = styled.div`
  display: block;
  @media (max-width: 1024px) {
    display: none;
  }
`

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  width: 100%;
  min-width: 0;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    padding: 0 12px;
    box-sizing: border-box;
  }

  @media (max-width: 360px) {
    grid-template-columns: 1fr;
    padding: 0 10px;
  }
`

const UnifiedCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.03);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
    border-color: ${({ theme }) => theme.colors.primary}30;
  }
`

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f5f5f5;
  gap: 10px;
`

const CardTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  line-height: 1.4;
`

const CardBody = styled.div`
  flex: 1;
`

const InfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
`

const Label = styled.span`
  color: #111827;
  font-weight: 500;
`

const Value = styled.span`
  color: ${({ theme }) => theme.colors.haiti};
  font-weight: 600;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 65%;
`

const StatusBadgeMobile = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  background-color: ${props => props.bg || '#36becd'};
  color: ${props => props.text || '#ffffff'};
  white-space: nowrap;
`

const FavIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${({ $isActive }) => $isActive ? '#fadb14' : '#b0b0b0'};
  transition: transform 0.2s, color 0.2s;

  &:hover {
    transform: scale(1.1);
    color: ${({ $isActive }) => $isActive ? '#fadb14' : '#faad14'};
  }

  svg {
    font-size: 20px;
  }
`
